import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import { Skeleton } from "@/design-system/Skeleton/Skeleton";
import { cn } from "@/lib/utils";
import type { CheapestItemScoreRow } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { ArrowDown, ArrowUp, Star } from "lucide-react";
import { useMemo, useState } from "react";
import type React from "react";

type SortKey = "rating" | "price" | "itemScore" | "ratio";
type SortDirection = "asc" | "desc";

const SORT_VALUE: Record<SortKey, (row: CheapestItemScoreRow) => number> = {
  rating: (row) => row.ratingFrom,
  price: (row) => row.price,
  itemScore: (row) => row.itemScore,
  ratio: (row) => row.ratio,
};
const SKELETON_ROWS = 8;
const DIVIDED_BY = "/";
const formatNumber = (value: number) => value.toLocaleString("en-US");

export const CheapestItemScoreTable = ({
  rows,
  loading,
}: {
  rows: CheapestItemScoreRow[];
  loading: boolean;
}) => {
  const { t } = useTranslation(["cheapestItemScore", "common"]);
  const coinAssetUrl = `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/coin-tmp.png`;
  const [sortBy, setSortBy] = useState<SortKey>("ratio");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // The best deal keeps its mark whatever the viewer sorts by.
  const bestRatio = rows.length
    ? Math.min(...rows.map((row) => row.ratio))
    : undefined;

  const sorted = useMemo(() => {
    const value = SORT_VALUE[sortBy];
    const sign = sortDirection === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => (value(a) - value(b)) * sign);
  }, [rows, sortBy, sortDirection]);

  const onSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // A fresh column opens descending; only the initial ratio sort is ascending.
      setSortBy(key);
      setSortDirection("desc");
    }
  };

  const cell = "px-3 py-3.5 mobile:px-5 whitespace-nowrap";
  const columns: {
    key: SortKey;
    label: string;
    width: string;
    alignRight?: boolean;
    prefix?: React.ReactNode;
    /** Drop the text label below the tablet breakpoint; the prefix stands alone. */
    iconOnlyOnPhone?: boolean;
  }[] = [
    {
      key: "rating",
      label: t("table.rating"),
      width: "w-[19%] mobile:w-[15%]",
    },
    {
      key: "price",
      label: t("table.cheapest"),
      width: "w-[27%]",
      alignRight: true,
    },
    {
      key: "itemScore",
      label: t("table.itemScore"),
      width: "w-[30%] mobile:w-[27%]",
      alignRight: true,
    },
    {
      key: "ratio",
      label: t("table.ratio"),
      width: "w-[24%] mobile:w-[31%]",
      alignRight: true,
      // coin / diamond Ratio: the unit lives in the header, not in every row.
      prefix: (
        <span className="inline-flex items-center gap-0.5">
          <img
            src={coinAssetUrl}
            alt={t("common:alt.coin")}
            className="h-3.5 w-3.5"
          />
          {DIVIDED_BY}
          <ItemScoreDiamond size={12} />
        </span>
      ),
      iconOnlyOnPhone: true,
    },
  ];

  return (
    <div className="overflow-x-auto rounded-2xl border border-neutral-700/70">
      <table className="w-full table-fixed text-left text-sm">
        <thead>
          <tr className="bg-panel-background text-xs uppercase tracking-wider text-foreground-muted">
            {columns.map(
              ({ key, label, width, alignRight, prefix, iconOnlyOnPhone }) => {
                const active = sortBy === key;
                return (
                  <th
                    key={key}
                    aria-sort={
                      active
                        ? sortDirection === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                    className={cn(
                      cell,
                      width,
                      "font-medium",
                      alignRight && "text-right",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onSort(key)}
                      className={cn(
                        "inline-flex items-center gap-1 uppercase tracking-wider hover:text-white",
                        active && "text-white",
                      )}
                    >
                      {prefix}
                      <span
                        className={cn(
                          iconOnlyOnPhone && "hidden mobile:inline",
                        )}
                      >
                        {label}
                      </span>
                      {active &&
                        (sortDirection === "asc" ? (
                          <ArrowUp size={12} className="shrink-0" />
                        ) : (
                          <ArrowDown size={12} className="shrink-0" />
                        ))}
                    </button>
                  </th>
                );
              },
            )}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: SKELETON_ROWS }, (_, i) => (
                <tr
                  key={i}
                  className="border-t border-neutral-800 odd:bg-white/[0.02]"
                >
                  {columns.map(({ key }) => (
                    <td key={key} className={cell}>
                      <Skeleton className="h-4 w-14 ml-auto first:ml-0" />
                    </td>
                  ))}
                </tr>
              ))
            : sorted.map((row) => {
                const isBest = row.ratio === bestRatio;
                return (
                  <tr
                    key={row.ratingFrom}
                    className={cn(
                      "border-t border-neutral-800 odd:bg-white/[0.02]",
                      isBest && "bg-primary-blue/10 odd:bg-primary-blue/10",
                    )}
                  >
                    <td className={`${cell} font-semibold tabular-nums`}>
                      {row.ratingFrom === row.ratingTo
                        ? row.ratingFrom
                        : t("table.ratingRange", {
                            from: row.ratingFrom,
                            to: row.ratingTo,
                          })}
                    </td>
                    <td className={`${cell} text-right`}>
                      <span className="inline-flex items-center gap-1.5 tabular-nums">
                        <img
                          src={coinAssetUrl}
                          alt={t("common:alt.coin")}
                          className="h-4 w-4"
                        />
                        {formatNumber(row.price)}
                      </span>
                    </td>
                    <td className={`${cell} text-right`}>
                      <span className="inline-flex items-center gap-1.5 tabular-nums">
                        <ItemScoreDiamond size={13} />
                        {formatNumber(row.itemScore)}
                      </span>
                    </td>
                    <td
                      className={`${cell} text-right font-semibold tabular-nums`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {isBest && (
                          <Star
                            size={13}
                            className="mr-0.5 shrink-0 fill-primary-blue text-primary-blue"
                            aria-label={t("table.bestValue")}
                          />
                        )}
                        <span>{row.ratio.toFixed(2)}</span>
                      </span>
                    </td>
                  </tr>
                );
              })}
        </tbody>
      </table>
    </div>
  );
};
